import Swiper from 'swiper';
import { A11y, Keyboard, Navigation, Pagination } from 'swiper/modules';
import { decorateCarouselPagination, syncNonLoopCarouselNavState, updateCarouselLiveRegion } from './utils/lvzCarouselA11y';

(function initLvzMediaCarousel() {
	/**
	 * Page Designer adds an extra wrapper around region children.
	 * Swiper needs the direct children of `.swiper-wrapper` to carry `.swiper-slide`,
	 * so we promote the class from the inner rendered component root to the PD wrapper.
	 */
	function normalizePdSwiperSlides(swiperRoot) {
		const wrapper = swiperRoot.querySelector('.swiper-wrapper');
		if (!wrapper) {
			return;
		}

		Array.prototype.forEach.call(wrapper.children, function (child) {
			if (child.classList.contains('swiper-slide')) {
				return;
			}

			const nested = child.querySelector(':scope > .swiper-slide');
			if (nested) {
				child.classList.add('swiper-slide');
				nested.classList.remove('swiper-slide');
			}
		});
	}

	/**
	 * Initialize one carousel instance.
	 * The server already blocks storefront rendering below 3 slides; this guard keeps the
	 * client script aligned with that contract and avoids mounting Swiper on preview markup.
	 */
	function initOne(root) {
		if (root.getAttribute('data-lvz-media-carousel-init') === 'true') {
			return;
		}

		if (root.getAttribute('data-renderable') !== 'true') {
			return;
		}

		const swiperEl = root.querySelector('[data-lvz-media-carousel-swiper="true"]');
		if (!swiperEl) {
			return;
		}

		const slideCount = Number.parseInt(root.getAttribute('data-slide-count'), 10);
		if (Number.isNaN(slideCount) || slideCount < 3) {
			return;
		}

		const paginationPattern = root.getAttribute('data-dot-label-pattern') || 'Image {0} of {1}';
		const livePattern = root.getAttribute('data-live-label-pattern') || paginationPattern;
		const prevButton = swiperEl.querySelector('.lvz-media-carousel__nav--prev');
		const nextButton = swiperEl.querySelector('.lvz-media-carousel__nav--next');
		const paginationEl = swiperEl.querySelector('.lvz-media-carousel__pagination');
		const liveEl = root.querySelector('.lvz-media-carousel__live');
		const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches === true;

		normalizePdSwiperSlides(swiperEl);

		const swiper = new Swiper(swiperEl, {
			modules: [Navigation, Pagination, A11y, Keyboard],
			loop: false,
			speed: reduceMotion ? 0 : 300,
			slidesPerView: 'auto',
			spaceBetween: 16,
			slidesOffsetBefore: 24,
			slidesOffsetAfter: 24,
			grabCursor: true,
			watchOverflow: true,
			observer: true,
			observeParents: true,
			a11y: {
				enabled: true,
			},
			keyboard: {
				enabled: true,
				onlyInViewport: true,
			},
			// Mobile/tablet (Figma): 297px-equivalent fluid slides from a 24px inset, peek of the next.
			pagination: paginationEl
				? {
						el: paginationEl,
						clickable: true,
				  }
				: undefined,
			navigation:
				prevButton && nextButton
					? {
							prevEl: prevButton,
							nextEl: nextButton,
							enabled: false,
							addIcons: false,
					  }
					: undefined,
			breakpoints: {
				// Desktop: 480px-equivalent fluid slides, two full plus a peek at 1440px.
				1024: {
					slidesPerView: 'auto',
					spaceBetween: 16,
					slidesOffsetBefore: 160,
					slidesOffsetAfter: 160,
					navigation:
						prevButton && nextButton
							? {
									prevEl: prevButton,
									nextEl: nextButton,
									enabled: true,
									addIcons: false,
							  }
							: undefined,
				},
			},
			on: {
				init: function onInit(sw) {
					decorateCarouselPagination(root, '.lvz-media-carousel__pagination', paginationPattern, slideCount);
					updateCarouselLiveRegion(liveEl, livePattern, sw.activeIndex, slideCount);
					syncNonLoopCarouselNavState(sw, prevButton, nextButton);
				},
				slideChange: function onSlideChange(sw) {
					updateCarouselLiveRegion(liveEl, livePattern, sw.activeIndex, slideCount);
					syncNonLoopCarouselNavState(sw, prevButton, nextButton);
				},
				reachBeginning: function onReachBeginning(sw) {
					syncNonLoopCarouselNavState(sw, prevButton, nextButton);
				},
				reachEnd: function onReachEnd(sw) {
					syncNonLoopCarouselNavState(sw, prevButton, nextButton);
				},
				fromEdge: function onFromEdge(sw) {
					syncNonLoopCarouselNavState(sw, prevButton, nextButton);
				},
				breakpoint: function onBreakpoint(sw) {
					decorateCarouselPagination(root, '.lvz-media-carousel__pagination', paginationPattern, slideCount);
					syncNonLoopCarouselNavState(sw, prevButton, nextButton);
				},
			},
		});

		root.setAttribute('data-lvz-media-carousel-init', 'true');

		requestAnimationFrame(function () {
			swiper.update();
			syncNonLoopCarouselNavState(swiper, prevButton, nextButton);
		});
		window.addEventListener(
			'resize',
			function onResize() {
				// Swiper already handles breakpoints, but an explicit update keeps measurements
				// stable when editorial content or viewport width changes after mount.
				swiper.update();
				syncNonLoopCarouselNavState(swiper, prevButton, nextButton);
			},
			{ passive: true }
		);
	}

	function boot() {
		document.querySelectorAll('[data-lvz-media-carousel]').forEach(function (root) {
			initOne(root);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', boot);
	} else {
		boot();
	}
})();
